import React, { useState, useEffect } from "react";
import "./Match.css";
import '@flaticon/flaticon-uicons/css/all/all.css';
import { database } from '../../../utils/firebase';
import { ref, onValue, set } from 'firebase/database';
import { useNavigate, useParams } from "react-router-dom";
import {
    Breadcrumbs,
    Button, Card, CardContent,
    Dropdown,
    IconButton,
    Input,
    Link,
    Menu,
    MenuButton,
    MenuItem,
    Tooltip,
    Typography,
    Option,
    Select, Autocomplete
} from "@mui/joy";
import PrintIcon from "@mui/icons-material/Print";
import QuickReportGenerator from "../QuickReportGenerator/QuickReportGenerator";
import {FilterList, SwapVert} from "@mui/icons-material";
import {Popover, TextField} from "@mui/material";

const Match = () => {
    const navigate = useNavigate();
    const { leagueId, matchId } = useParams();

    const [isLocked, setIsLocked] = useState(true);
    const [isFinalized, setIsFinalized] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [scores, setScores] = useState({});
    const [handicaps, setHandicaps] = useState({});
    const [payouts, setPayouts] = useState({});
    const [otherPayouts, setOtherPayouts] = useState([]);
    const [leagueData, setLeagueData] = useState({});
    const [matchData, setMatchData] = useState({});
    const [playerIds, setPlayerIds] = useState([]);

    const [reportModalOpen, setReportModalOpen] = useState(false);
    const [reportType, setReportType] = useState('');

    const [searchQuery, setSearchQuery] = useState('');
    const [sortField, setSortField] = useState("ln");
    const [sortAsc, setSortAsc] = useState(true);

    const [anchorEl, setAnchorEl] = useState(null);
    const filterMenuOpen = Boolean(anchorEl);

    useEffect(() => {
        const fetchData = async () => {
            const leagueRef = ref(database, `leagues/${leagueId}`);
            const matchRef = ref(database, `leagues/${leagueId}/matches/${matchId}`);

            onValue(leagueRef, (leagueSnapshot) => {
                const leagueData = leagueSnapshot.val();
                setLeagueData(leagueData);

                onValue(matchRef, (matchSnapshot) => {
                    const matchData = matchSnapshot.val();
                    setMatchData(matchData);

                    const matchDate = matchData.dateTime * 1000; // Convert match date to milliseconds
                    const activePlayerIds = Object.keys(leagueData.players).filter(playerId => {
                        const player = leagueData.players[playerId];
                        const playerStartDate = player.startDate;
                        const playerEndDate = player.endDate || Infinity; // Use Infinity if no end date is provided
                        return (player.status === 'active' && playerStartDate <= matchDate) || (playerStartDate <= matchDate && matchDate <= playerEndDate);
                    });

                    setPlayerIds(activePlayerIds);

                    // Initialize scores, handicaps, and payouts from the database
                    const initialScores = {};
                    const initialHandicaps = {};
                    const initialPayouts = {};

                    activePlayerIds.forEach(playerId => {
                        initialScores[playerId] = null;
                        initialHandicaps[playerId] = null; // Default to null if no valid handicap found
                        initialPayouts[playerId] = { total: null, details: [] };

                        // Find the correct handicap set that matches the match date range
                        const relevantHandicapSet = Object.values(leagueData.handicaps).find(handicapSet => {
                            return handicapSet.startDate * 1000 <= matchDate && matchDate <= (handicapSet.endDate || Infinity) * 1000;
                        });

                        if (relevantHandicapSet && relevantHandicapSet.playerHandicaps[playerId] !== undefined) {
                            initialHandicaps[playerId] = relevantHandicapSet.playerHandicaps[playerId];
                        }
                    });

                    setScores(initialScores);
                    setHandicaps(initialHandicaps);

                    if (matchData && matchData.scores) {
                        Object.keys(matchData.scores).forEach(playerId => {
                            initialScores[playerId] = matchData.scores[playerId].gross;
                            initialPayouts[playerId] = matchData.scores[playerId].payouts || { total: null, details: [] };
                        });
                        setScores(initialScores);
                        setPayouts(initialPayouts);
                    }

                    if (matchData && matchData.finalized) {
                        setIsFinalized(matchData.finalized);
                    }

                    // Find the relevant payout set based on the match date
                    const relevantPayoutSet = Object.values(leagueData.payouts).find(payoutSet => {
                        return payoutSet.startDate * 1000 <= matchDate && matchDate <= payoutSet.endDate * 1000;
                    });

                    if (relevantPayoutSet && relevantPayoutSet.otherPayouts) {
                        const otherPayoutsData = relevantPayoutSet.otherPayouts.map((payoutInfo, index) => {
                            const people = matchData.otherPayouts && matchData.otherPayouts[index] ? matchData.otherPayouts[index] : {};
                            return {
                                id: index,
                                name: payoutInfo.name,
                                amount: payoutInfo.amount,
                                holeBound: payoutInfo.holeBound,
                                people: Object.entries(people).map(([key, playerId]) => ({
                                    playerId,
                                    holeNumber: payoutInfo.holeBound ? key : '',
                                    value: payoutInfo.holeBound ? '' : key
                                }))
                            };
                        });
                        setOtherPayouts(otherPayoutsData);
                    }
                });
            });
        };

        fetchData();
    }, [leagueId, matchId]);

    const handleLockToggle = () => {
        setIsLocked(!isLocked);
    }

    const handleFinalizeScores = async () => {
        const confirmed = window.confirm("Are you sure you want to finalize the scores? This action CANNOT be undone.");
        if (!confirmed) return;

        try {
            const matchDate = matchData.dateTime * 1000;

            const matchUpdateRef = ref(database, `leagues/${leagueId}/matches/${matchId}`);
            await set(matchUpdateRef, {
                ...matchData,
                finalized: true
            });

            setIsFinalized(true);
            setIsLocked(true);

            const playersByFlight = {};
            const playerPayouts = {};
            const paidPlayerIds = new Set();

            const relevantHandicapSet = Object.values(leagueData.handicaps).find(set =>
                set.startDate * 1000 <= matchDate && matchDate <= set.endDate * 1000
            );
            const relevantPayoutSet = Object.values(leagueData.payouts).find(set =>
                set.startDate * 1000 <= matchDate && matchDate <= set.endDate * 1000
            );

            if (!relevantHandicapSet || !relevantPayoutSet) {
                console.error('Relevant handicap or payout set not found');
                return;
            }

            // Group players into flights
            Object.entries(relevantHandicapSet.flights).forEach(([flightId, flight]) => {
                const players = Object.entries(relevantHandicapSet.playerHandicaps)
                    .filter(([playerId, hc]) => +hc >= +flight.start && +hc <= +flight.end)
                    .map(([playerId]) => {
                        const score = matchData.scores[playerId];
                        if (score && score.gross > 0) {
                            return {
                                playerId,
                                name: leagueData.players[playerId]?.name || 'Unknown',
                                gross: score.gross,
                                net: score.net
                            };
                        }
                        return null;
                    }).filter(Boolean);
                playersByFlight[flightId] = players;
            });

            // Process main payouts
            for (const payoutGroup of Object.values(relevantPayoutSet.mainPayouts)) {
                const payouts = Object.values(payoutGroup.payouts).sort((a, b) => a.priority - b.priority);

                for (const payout of payouts) {
                    for (const [flightId, originalPlayers] of Object.entries(playersByFlight)) {
                        // Skip if no eligible players
                        const eligible = originalPlayers.filter(p => !paidPlayerIds.has(p.playerId));
                        if (eligible.length === 0) continue;

                        const getScore = p => payout.type === 'net' ? p.net : p.gross;
                        let bestScore = Math.min(...eligible.map(getScore));
                        let bestPlayers = eligible.filter(p => getScore(p) === bestScore);

                        if (bestPlayers.length === 0) continue;

                        if (payout.tieBehavior === 'payAll') {
                            bestPlayers.forEach(player => {
                                awardPayout(player, payout);
                                paidPlayerIds.add(player.playerId);
                            });
                        } else if (payout.tieBehavior === 'split') {
                            const splitAmount = parseFloat(payout.amount) / bestPlayers.length;
                            bestPlayers.forEach(player => {
                                awardPayout(player, { ...payout, amount: splitAmount });
                                paidPlayerIds.add(player.playerId);
                            });
                        } else if (payout.tieBehavior === 'payNone') {
                            // Do nothing
                        }

                        // Remove awarded players from flight for next payout
                        playersByFlight[flightId] = originalPlayers.filter(p => !paidPlayerIds.has(p.playerId));
                    }
                }
            }

            // Handle other payouts (Closest to the Pin, etc.)
            matchData.otherPayouts?.forEach(payout => {
                Object.entries(payout).forEach(([hole, playerId]) => {
                    const amount = parseFloat(relevantPayoutSet.otherPayouts?.[0]?.amount || 0);
                    const name = `${relevantPayoutSet.otherPayouts?.[0]?.name} Hole #${hole}`;
                    awardPayout({ playerId }, { name, amount });
                });
            });

            // Write payouts to DB
            await Promise.all(Object.entries(playerPayouts).map(([playerId, payoutData]) => {
                const refPath = ref(database, `leagues/${leagueId}/matches/${matchId}/scores/${playerId}/payouts`);
                return set(refPath, { total: payoutData.total, details: payoutData.payouts });
            }));

            // Helper function to update payouts
            function awardPayout(player, payout) {
                if (!playerPayouts[player.playerId]) {
                    playerPayouts[player.playerId] = { total: 0, payouts: [] };
                }
                playerPayouts[player.playerId].total += parseFloat(payout.amount);
                playerPayouts[player.playerId].payouts.push({
                    name: payout.name,
                    amount: parseFloat(payout.amount)
                });
            }

        } catch (error) {
            console.error('Error during finalization:', error);
        }
    };

    const handleScoreChange = (playerId, e) => {
        const newScores = { ...scores, [playerId]: e.target.value };
        setScores(newScores);
        setHasUnsavedChanges(true);
    };

    const handleSave = () => {
        playerIds.forEach(playerId => {
            const grossScore = parseInt(scores[playerId], 10);
            const handicap = handicaps[playerId];
            const netScore = grossScore - handicap;

            // Ensure netScore is a valid number
            if (isNaN(netScore)) {
                console.error(`Invalid net score for player ${playerId}: grossScore=${grossScore}, handicap=${handicap}`);
                return;
            }

            const playerScoreRef = ref(database, `leagues/${leagueId}/matches/${matchId}/scores/${playerId}`);
            const updates = {
                net: netScore,
                gross: grossScore
            };

            set(playerScoreRef, updates)
                .then(() => {
                    setHasUnsavedChanges(false);
                    console.log('Scores saved successfully');
                })
                .catch(error => {
                    console.error('Error saving scores:', error);
                });
        });

        otherPayouts.forEach(payout => {
            const payoutRef = ref(database, `leagues/${leagueId}/matches/${matchId}/otherPayouts/${payout.id}`);
            const payoutData = payout.people.reduce((acc, person) => {
                const key = payout.holeBound ? person.holeNumber : person.playerId;
                acc[key] = person.playerId;
                return acc;
            }, {});
            set(payoutRef, payoutData)
                .then(() => {
                    console.log('Other payouts saved successfully');
                })
                .catch(error => {
                    console.error('Error saving other payouts:', error);
                });
        });
    };

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [hasUnsavedChanges]);

    const handleUnixTime = (unixTime) => {
        const date = new Date(unixTime * 1000);
        const options = {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
            timeZone: leagueData.settings.timeZone
        };
        return date.toLocaleString(leagueData.settings.language, options);
    };

    const handleUnixTimeShort = (unixTime) => {
        const date = new Date(unixTime * 1000);
        const options = {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
            timeZone: leagueData.settings.timeZone
        };
        return date.toLocaleString(leagueData.settings.language, options);
    };

    const addPersonToPayout = (payoutIndex) => {
        const newOtherPayouts = [...otherPayouts];
        newOtherPayouts[payoutIndex].people = newOtherPayouts[payoutIndex].people || [];
        const newPerson = { playerId: '', holeNumber: '' };
        newOtherPayouts[payoutIndex].people.push(newPerson);
        setOtherPayouts(newOtherPayouts);
        setHasUnsavedChanges(true);
    };

    const removePersonFromPayout = (payoutIndex, personIndex) => {
        const newOtherPayouts = [...otherPayouts];
        newOtherPayouts[payoutIndex].people.splice(personIndex, 1);
        setOtherPayouts(newOtherPayouts);
        setHasUnsavedChanges(true);
    };

    return (
        <div className={'container'}>
            <Breadcrumbs aria-label="breadcrumb">
                <Link underline="hover" color="inherit" onClick={() => navigate(`/app/league/${leagueId}/dashboard`)}>
                    {leagueData.name}
                </Link>
                <Link underline="hover" color="inherit" onClick={() => navigate(`/app/league/${leagueId}/dashboard`)}>
                    Matches
                </Link>
                <Typography sx={{ color: 'text.primary' }}>{matchData && matchData.dateTime ? handleUnixTimeShort(matchData.dateTime) : 'Loading...'}</Typography>
            </Breadcrumbs>
            <div className={"page-head-div"}>
                <div>
                    <h1>Match</h1>
                    <p className="ldb-location-name">{matchData && matchData.dateTime ? handleUnixTime(matchData.dateTime) : 'Loading...'}</p>
                </div>
                <Dropdown>
                    <MenuButton variant="plain" sx={{width: "35px", height: "35px"}}><PrintIcon/></MenuButton>
                    <Menu>
                        <MenuItem onClick={
                            () => {
                                setReportModalOpen(true);
                                setReportType('matchReport');
                            }}>Match Report</MenuItem>
                        <MenuItem onClick={
                            () => {
                                setReportModalOpen(true);
                                setReportType('winnersReport');
                            }}>Winners Report</MenuItem>
                        <MenuItem onClick={
                            () => {
                                setReportModalOpen(true);
                                setReportType('blankScoreSheet');
                            }}>Blank Score Sheet</MenuItem>
                    </Menu>
                </Dropdown>
            </div>
            <div className={'dashboard'}>
                <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px'}}>
                    <Button className={'button-gradient'} onClick={handleLockToggle} disabled={isFinalized}>
                        {isFinalized ? (
                            <Tooltip title={"Once scores are finalized they cannot be edited"}>
                                <div className={'lock-button'}><i className="fi fi-rr-lock white-icon"></i><p>Scores Finalized</p></div>
                            </Tooltip>
                        ) : (
                            !isLocked ? (
                                <div className={'lock-button'}><i className="fi fi-rr-unlock white-icon"></i><p>Unlocked</p></div>
                            ) : (
                                <div className={'lock-button'}><i className="fi fi-rr-lock white-icon"></i><p>Locked</p></div>
                            )
                        )}
                    </Button>
                    <div style={{ display: 'flex', gap: "10px" }}>
                        <Input
                            type="text"
                            placeholder="Search players..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full"
                        />
                        <IconButton variant="plain" onClick={(e) => setAnchorEl(e.currentTarget)}>
                            <FilterList />
                        </IconButton>
                        <Popover
                            open={filterMenuOpen}
                            anchorEl={anchorEl}
                            onClose={() => setAnchorEl(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            sx={{ zIndex: 1, '& .MuiPaper-root': { backgroundColor: 'transparent', boxShadow: 'none' } }}
                        >
                            <Card>
                                <CardContent>
                                    <Typography level="title-md">Sort By:</Typography>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Select value={sortField} onChange={(e, newValue) => setSortField(newValue)}>
                                            <Option value={"ln"}>Last Name</Option>
                                            <Option value={"fn"}>First Name</Option>
                                            {/*<Option value={"jd"}>Join Date</Option>*/}
                                        </Select>
                                        <IconButton onClick={() => setSortAsc(prev => !prev)} variant="outlined">
                                            <SwapVert />
                                        </IconButton>
                                    </div>
                                </CardContent>
                            </Card>
                        </Popover>
                    </div>
                </div>
                {playerIds
                    .filter((id) => {
                        const player = leagueData.players[id];

                        return !(searchQuery.trim() &&
                            !player.name.toLowerCase().includes(searchQuery.toLowerCase()));
                    })
                    .sort((aId, bId) => {
                        const a = leagueData.players[aId];
                        const b = leagueData.players[bId];
                        let aVal = a.name;
                        let bVal = b.name;

                        if (sortField === 'jd') {
                            aVal = a.startDate;
                            bVal = b.startDate;
                        } else if (sortField === 'fn' || sortField === 'ln') {
                            const aSplit = a.name.split(" ");
                            const bSplit = b.name.split(" ");
                            aVal = sortField === 'fn' ? aSplit[0] : aSplit[aSplit.length - 1];
                            bVal = sortField === 'fn' ? bSplit[0] : bSplit[bSplit.length - 1];
                        }

                        if (aVal < bVal) return sortAsc ? -1 : 1;
                        if (aVal > bVal) return sortAsc ? 1 : -1;
                        return 0;
                    })
                    .map((playerId) => (
                    <div className="ldb-match" key={playerId}>
                        <div className={'match-person-content'}>
                            <p className={'match-person-name'}>{leagueData.players[playerId].name}</p>
                            {isLocked ? (
                                <Tooltip title={"Scores are locked"}>
                                    <input className={'match-score-input styled-select'} disabled={true} value={scores[playerId] || ''} />
                                </Tooltip>
                            ) : (
                                <input className={'match-score-input styled-select'} disabled={false} value={scores[playerId] || ''} onChange={(e) => handleScoreChange(playerId, e)} />
                            )}
                            <Tooltip title={"Handicaps can only be edited in the roster"}>
                                <input className={'match-score-input styled-select'} disabled={true} value={handicaps[playerId] || ''} />

                            </Tooltip>
                            <Tooltip title={"Net scores are calculated automatically"}>
                                <input className={'match-score-input styled-select'} disabled={true} value={(scores[playerId] !== null && handicaps[playerId] !== null) ? scores[playerId] - handicaps[playerId] : ''} />
                            </Tooltip>
                            {payouts[playerId] && payouts[playerId].details.length > 0 && (
                                <div className={'payout-details'}>
                                    <p>{`$${payouts[playerId].total !== null ? payouts[playerId].total.toFixed(2) : '0.00'}: ${payouts[playerId].details.map(payout => `${payout.name}`).join(', ')}`}</p>
                                </div>
                            )}
                            {payouts[playerId] && console.log(`Player ID: ${playerId}, Payout Details:`, payouts[playerId].details)}
                        </div>
                    </div>
                ))}
                {otherPayouts.map((payout, payoutIndex) => (
                    <div key={payoutIndex} className="payout-group-card">
                        <h3>{payout.name}</h3>
                        {payout.people && payout.people.map((person, personIndex) => (
                            <div key={personIndex} className="ldb-match" style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                                <div style={{display: "flex", flexDirection: "row", gap: "10px"}}>
                                    {payout.holeBound && (
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <span style={{ marginRight: '5px' }}>#</span>
                                            <input
                                                disabled={isLocked}
                                                className={'match-score-input styled-select'}
                                                value={person.holeNumber}
                                                onChange={(e) => {
                                                    const newOtherPayouts = [...otherPayouts];
                                                    newOtherPayouts[payoutIndex].people[personIndex].holeNumber = e.target.value;
                                                    setOtherPayouts(newOtherPayouts);
                                                    setHasUnsavedChanges(true);
                                                }}
                                            />
                                        </div>
                                    )}

                                    <Autocomplete
                                        disableClearable
                                        autoComplete
                                        autoSelect
                                        autoHighlight
                                        options={playerIds.map(playerId => ({
                                            value: playerId,
                                            label: leagueData.players[playerId]?.name || "Unknown Player"
                                        }))}
                                        getOptionLabel={(option) => option.label}
                                        disabled={isLocked}
                                        value={playerIds
                                            .map(playerId => ({
                                                value: playerId,
                                                label: leagueData.players[playerId]?.name || "Unknown Player"
                                            }))
                                            .find(option => option.value === person.playerId) || null}
                                        onChange={(event, newValue) => {
                                            if (newValue && newValue.value) {
                                                const newOtherPayouts = [...otherPayouts];
                                                newOtherPayouts[payoutIndex].people[personIndex].playerId = newValue.value;
                                                setOtherPayouts(newOtherPayouts);
                                                setHasUnsavedChanges(true);
                                            } else {
                                                console.error("Invalid selection: newValue is null or undefined");
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Select Player" variant="outlined" />
                                        )}
                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                    />
                                </div>
                                {isLocked ? (
                                    <Tooltip title={"Scores are locked"}>
                                        <Button disabled onClick={() => removePersonFromPayout(payoutIndex, personIndex)} className={'button-gradient'}><i className={'fi-rr-trash'}></i></Button>
                                    </Tooltip>
                                ) : (
                                    <Button disabled={isLocked} onClick={() => removePersonFromPayout(payoutIndex, personIndex)} className={'button-gradient'}><i className={'fi-rr-trash'}></i></Button>
                                )}
                                
                            </div>
                        ))}
                        {isLocked ? (
                            <Tooltip title={"Scores are locked"}>
                                <Button disabled onClick={() => addPersonToPayout(payoutIndex)} className={'button-gradient'} style={{width: "120px"}}>Add Person</Button>
                            </Tooltip>
                        ) : (
                            <Button onClick={() => addPersonToPayout(payoutIndex)} className={'button-gradient'} style={{width: "120px"}}>Add Person</Button>
                        )}
                    </div>
                ))}                <Button className={'button-gradient'} onClick={handleSave}>Save</Button>
                <Button className={'button-gradient'} onClick={handleFinalizeScores} style={{marginLeft: "10px"}} disabled={hasUnsavedChanges}>Finalize Scores</Button>
            </div>
            <QuickReportGenerator
                type={reportType}
                id={matchId}
                modalOpen={reportModalOpen}
                closeModal={() => setReportModalOpen(false)}
                leagueId={leagueId}
            />
        </div>
    );
}

export default Match;